import { Link } from "react-router";
import { ChevronLeft } from "lucide-react";
import { StatusPill, type BlogStatus } from "../blog-list/StatusPill";

interface Props {
  title: string;
  status: BlogStatus;
  isDirty: boolean;
  isSaving: boolean;
  saveLabel: string;
  canSave: boolean;
  onDiscard?: () => void;
  onSave: () => void;
}

/**
 * Sticky header for the blog post editor. Holds the back link and
 * title breadcrumb on the left, the live status pill in the middle,
 * and the discard/save actions on the right. Mirrors the AgreementEdit
 * top bar so navigating between editors feels consistent.
 */
export function EditorTopBar({
  title,
  status,
  isDirty,
  isSaving,
  saveLabel,
  canSave,
  onDiscard,
  onSave,
}: Props) {
  return (
    <div className="hair-b border-b sticky top-[var(--banner-h)] z-20 bg-paper/90 backdrop-blur">
      <div className="flex h-14 items-center justify-between gap-4 px-7">
        <div className="flex min-w-0 items-center gap-3">
          <Link
            to="/website/blog"
            className="flex flex-shrink-0 items-center gap-1.5 text-[12.5px] text-ink-4 hover:text-ink-2"
          >
            <ChevronLeft size={14} strokeWidth={2} />
            Blog
          </Link>
          <span className="flex-shrink-0 text-ink-5">›</span>
          <span className="truncate text-[13.5px] font-medium text-ink">
            {title || "Untitled post"}
          </span>
          <StatusPill status={status} />
        </div>
        <div className="flex flex-shrink-0 items-center gap-2">
          {isDirty && !isSaving && (
            <span className="mr-1 text-[12px] text-ink-4">Unsaved</span>
          )}
          {onDiscard && (
            <button
              type="button"
              onClick={onDiscard}
              disabled={!isDirty || isSaving}
              className="hair rounded-md border bg-paper-hi px-3 py-1.5 text-[12.5px] text-ink-3 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Discard
            </button>
          )}
          <button
            type="button"
            onClick={onSave}
            disabled={!canSave}
            className="rounded-md bg-tide px-3.5 py-1.5 text-[12.5px] font-medium text-paper-hi hover:bg-tide-d disabled:cursor-not-allowed disabled:bg-ink-6"
          >
            {isSaving ? "Saving…" : saveLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
